import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery } from "react-query";
import { AxiosError } from "axios";
import {
	Button,
	Card,
	Col,
	Form,
	InputGroup,
	ListGroup,
	Modal,
	Row,
	Spinner,
} from "react-bootstrap";
import Icon from "@mdi/react";
import { mdiCardRemoveOutline, mdiPlus, mdiTrashCanOutline } from "@mdi/js";

import { DomainEntityGetResponse } from "src/entities/DomainEntity";
import { CriteriaDomainGetResponse } from "src/entities/CriteriaDomainEntity";
import DomainService from "src/services/DomainService";
import CriteriaDomainService, { criteriaDomainQueryKeys } from "src/services/CriteriaDomainService";
import { useToast } from "src/context/ToastContext";
import Navigation from "./Navigation";
import { clearCriteriaStepDraft, getCriteriaStepDraft, setCriteriaStepDraft } from "./draftStorage";

const domainService = new DomainService();
const criteriaDomainService = new CriteriaDomainService();

type Domain = {
	domainId: number;
	criteriaDomainId?: number;
	title: string;
	required: boolean;
	originalRequired?: boolean;
	options: DomainOption[];
};

type DomainOption = {
	domainOptionId: number;
	criteriaDomainOptionId?: number;
	title: string;
	value: number | null;
	enabled: boolean;
	originalEnabled?: boolean;
};

type StepDomainsDraft = {
	domains: Domain[];
	removedIds: number[];
};

type StepDomainsProps = {
	criteriaId: number;
	backLabel: string;
	nextLabel: string;
	onBack: () => void;
	onComplete: () => void;
};

function mapToDomain(domain: DomainEntityGetResponse): Domain {
	return {
		domainId: domain.id!,
		title: domain.title,
		required: true,
		options: domain.domainOptions.map((option) => ({
			domainOptionId: option.id!,
			title: option.title,
			value: option.value,
			enabled: true,
		})),
	};
}

type EmptyStateProps = {
	onAdd: () => void;
};

function EmptyState({ onAdd }: EmptyStateProps) {
	return (
		<Card className="mb-4">
			<Card.Header className="d-flex align-items-center justify-content-between bg-white fs-5 fw-light p-3">
				Domínios
			</Card.Header>
			<Card.Body>
				<div className="text-center p-4 d-flex justify-content-center align-items-center text-muted">
					<Icon path={mdiCardRemoveOutline} size={1} className="me-2" />
					Nenhum registro adicionado.
				</div>
			</Card.Body>
			<Card.Footer className="bg-white">
				<AddDomainButton onClick={onAdd} />
			</Card.Footer>
		</Card>
	);
}

type AddDomainButtonProps = {
	onClick: () => void;
};

function AddDomainButton({ onClick }: AddDomainButtonProps) {
	return (
		<Button
			variant="light"
			className="d-flex gap-2 align-items-center rounded-3 shadow-sm"
			type="button"
			onClick={onClick}
		>
			<Icon path={mdiPlus} size={1} /> Adicionar Domínio
		</Button>
	);
}

type DomainOptionRowProps = {
	option: DomainOption;
	domainIndex: number;
	optionIndex: number;
	onEnabledChange: (domainIndex: number, optionIndex: number, value: boolean) => void;
};

function DomainOptionRow({
	option,
	domainIndex,
	optionIndex,
	onEnabledChange,
}: DomainOptionRowProps) {
	return (
		<Row className="mb-2 gy-2">
			<Col md={12} lg={5}>
				<InputGroup>
					<InputGroup.Text>
						<small className="text-muted">Título</small>
					</InputGroup.Text>
					<Form.Control type="text" disabled value={option.title} />
				</InputGroup>
			</Col>
			<Col md={6} lg={3}>
				<InputGroup>
					<InputGroup.Text>
						<small className="text-muted">Valor</small>
					</InputGroup.Text>
					<Form.Control type="text" disabled value={option.value ?? ""} />
				</InputGroup>
			</Col>
			<Col md={6} lg={4}>
				<InputGroup>
					<Form.Check
						type="switch"
						className="d-flex gap-2 pt-1"
						label={option.enabled ? "Habilitado" : "Desabilitado"}
						checked={option.enabled}
						onChange={(event) => onEnabledChange(domainIndex, optionIndex, event.target.checked)}
					/>
				</InputGroup>
			</Col>
		</Row>
	);
}

type DomainCardProps = {
	domain: Domain;
	domainIndex: number;
	onRemove: (index: number) => void;
	onRequiredChange: (index: number, value: boolean) => void;
	onOptionEnabledChange: (domainIndex: number, optionIndex: number, value: boolean) => void;
};

function DomainCard({
	domain,
	domainIndex,
	onRemove,
	onRequiredChange,
	onOptionEnabledChange,
}: DomainCardProps) {
	return (
		<Card className="mb-4">
			<Card.Header className="d-flex align-items-center bg-white fs-5 fw-light p-3">
				<div className="d-flex flex-column lh-1">
					<div className="fs-6">
						<small className="text-muted fw-light">Domínio</small>
					</div>
					{domain.title}
				</div>
				<div className="float-right ms-auto">
					<Button
						variant="light"
						className="d-flex rounded-3 shadow-sm"
						type="button"
						onClick={() => onRemove(domainIndex)}
					>
						<Icon path={mdiTrashCanOutline} size={1} className="text-danger" />
					</Button>
				</div>
			</Card.Header>
			<Card.Body>
				<Form.Group className="mb-3 col-md-3">
					<Form.Label>Obrigatório</Form.Label>
					<Form.Check
						type="switch"
						className="pt-1"
						checked={domain.required}
						onChange={(event) => onRequiredChange(domainIndex, event.target.checked)}
					/>
				</Form.Group>
				{domain.options.map((option, optionIndex) => (
					<DomainOptionRow
						key={optionIndex}
						option={option}
						domainIndex={domainIndex}
						optionIndex={optionIndex}
						onEnabledChange={onOptionEnabledChange}
					/>
				))}
			</Card.Body>
		</Card>
	);
}

type AddDomainModalProps = {
	show: boolean;
	availableDomains: DomainEntityGetResponse[] | undefined;
	isLoading: boolean;
	onHide: () => void;
	onAdd: (domain: DomainEntityGetResponse) => void;
};

function AddDomainModal({
	show,
	availableDomains,
	isLoading,
	onHide,
	onAdd,
}: AddDomainModalProps) {
	return (
		<Modal show={show} onHide={onHide} centered>
			<Modal.Header closeButton>
				<Modal.Title>Adicionar Domínio</Modal.Title>
			</Modal.Header>
			<Modal.Body>
				{isLoading ? (
					<div className="text-center p-3">
						<Spinner size="sm" variant="secondary" />
					</div>
				) : (
					<ListGroup>
						{availableDomains?.map((domain, index) => (
							<ListGroup.Item key={index} action onClick={() => onAdd(domain)}>
								<span className="fw-medium">{domain.title}: </span>
								{domain.domainOptions.flatMap((item) => item.title).join(" | ") || "..."}
							</ListGroup.Item>
						))}
					</ListGroup>
				)}
			</Modal.Body>
		</Modal>
	);
}

export default function StepDomains({
	criteriaId,
	backLabel,
	nextLabel,
	onBack,
	onComplete,
}: StepDomainsProps) {
	const { handleToast } = useToast();
	const draftRef = useRef(getCriteriaStepDraft<StepDomainsDraft>(criteriaId, "domains"));
	const [domains, setDomains] = useState<Domain[]>(() => draftRef.current?.domains ?? []);
	const [removedIds, setRemovedIds] = useState<number[]>(() => draftRef.current?.removedIds ?? []);
	const [showModal, setShowModal] = useState(false);

	const criteriaIdStr = String(criteriaId);

	const { data: availableDomains, isLoading: availableDomainsLoading } =
		useQuery<DomainEntityGetResponse[]>(
			["dominios"],
			() => domainService.getDomains().then((response) => response.data),
			{ refetchOnWindowFocus: false }
		);

	const { data: existingDomainsData, isLoading: existingDomainsLoading } =
		useQuery<CriteriaDomainGetResponse>(
			criteriaDomainQueryKeys.domains(criteriaIdStr),
			() => criteriaDomainService.getDomains(criteriaId).then((response) => response.data),
			{ refetchOnWindowFocus: false }
		);

	useEffect(() => {
		if (!draftRef.current && existingDomainsData) {
			setDomains(existingDomainsData.domains.map((existing) => ({
				domainId: existing.domainId,
				criteriaDomainId: existing.criteriaDomainId,
				title: existing.title,
				required: existing.required,
				originalRequired: existing.required,
				options: existing.options.map((option) => ({
					domainOptionId: option.domainOptionId,
					criteriaDomainOptionId: option.criteriaDomainOptionId,
					title: option.title,
					value: option.value,
					enabled: option.enabled,
					originalEnabled: option.enabled,
				})),
			})));
		}
	}, [existingDomainsData]);

	const isLoading = existingDomainsLoading;

	const mutation = useMutation(
		async () => {
			const created = domains.filter((domain) => !domain.criteriaDomainId);
			const updated = domains.filter(
				(domain) => domain.criteriaDomainId && domain.required !== domain.originalRequired
			);
			const existing = domains.filter((domain) => domain.criteriaDomainId);

			await Promise.all([
				saveCreated(created),
				saveUpdated(updated),
				saveDeleted(),
				syncOptions(existing),
			]);
		},
		{
			onSuccess: () => {
				clearCriteriaStepDraft(criteriaId, "domains");
				onComplete();
			},
			onError: (err: AxiosError) => {
				handleToast("Critério", `Problema na operação: ${err.message}`, 5000, "danger");
			},
		}
	);

	function handleAdd(domain: DomainEntityGetResponse) {
		setDomains((prev) => [...prev, mapToDomain(domain)]);
		setShowModal(false);
	}

	function handleRemove(index: number) {
		const domain = domains[index];
		if (domain.criteriaDomainId) {
			setRemovedIds((prev) => [...prev, domain.criteriaDomainId!]);
		}
		setDomains((prev) => prev.filter((_, currentIndex) => currentIndex !== index));
	}

	function handleRequiredChange(index: number, value: boolean) {
		setDomains((prev) =>
			prev.map((domain, currentIndex) =>
				currentIndex === index ? { ...domain, required: value } : domain
			)
		);
	}

	function handleOptionEnabledChange(domainIndex: number, optionIndex: number, value: boolean) {
		setDomains((prev) =>
			prev.map((domain, currentDomainIndex) =>
				currentDomainIndex === domainIndex
					? 
						{
							...domain,
							options: domain.options.map((option, currentOptionIndex) =>
								currentOptionIndex === optionIndex ? { ...option, enabled: value } : option
							),
					 	 }
					: domain
			)
		);
	}

	async function saveCreated(created: Domain[]) {
		await Promise.all(
			created.map(async (domain) => {
				const response = await criteriaDomainService.postDomainRelation({
					required: domain.required,
					criteriaId,
					domainId: domain.domainId,
				});

				const criteriaDomainRelationId = response.data.id;

				await Promise.all(
					domain.options.map((option) =>
						criteriaDomainService.postDomainOption({
							enabled: option.enabled,
							criteriaDomainRelationId,
							domainOptionId: option.domainOptionId,
						})
					)
				);
			})
		);
	}

	async function saveUpdated(updated: Domain[]) {
		await Promise.all(
			updated.map((domain) =>
				criteriaDomainService.putDomainRelation({
					criteriaDomainId: domain.criteriaDomainId!,
					required: domain.required,
				})
			)
		);
	}

	async function saveDeleted() {
		await Promise.all(
			removedIds.map((id) => criteriaDomainService.deleteDomain(id))
		);
	}

	async function syncOptions(existing: Domain[]) {
		const calls: Promise<unknown>[] = [];

		existing.forEach((domain) => {
			domain.options.forEach((option) => {
				if (option.criteriaDomainOptionId && option.enabled !== option.originalEnabled) {
					calls.push(
						criteriaDomainService.putDomainOption({
							criteriaDomainOptionId: option.criteriaDomainOptionId,
							enabled: option.enabled,
						})
					);
				}
			});
		});

		await Promise.all(calls);
	}

	function handleBack() {
		setCriteriaStepDraft(criteriaId, "domains", { domains, removedIds });
		onBack();
	}

	return (
		<>
			{isLoading ? (
				<>
					{Array.from({ length: 2 }, (_, skeletonIndex) => (
						<Card key={skeletonIndex} className="mb-4">
							<Card.Header className="d-flex align-items-center bg-white fs-5 fw-light p-3">
								<div className="skeleton skeleton-text skeleton-text-title" style={{ height: 16, width: "20%" }} />
							</Card.Header>
							<Card.Body>
								<div className="skeleton skeleton-button mb-3" style={{ height: 24, width: "15%" }} />
								{Array.from({ length: 3 }, (_, rowIndex) => (
									<div key={rowIndex} className="skeleton skeleton-button mb-2" style={{ height: 38 }} />
								))}
							</Card.Body>
						</Card>
					))}
				</>
			) : domains.length === 0 ? (
				<EmptyState onAdd={() => setShowModal(true)} />
			) : (
				<>
					{domains.map((domain, domainIndex) => (
						<DomainCard
							key={domainIndex}
							domain={domain}
							domainIndex={domainIndex}
							onRemove={handleRemove}
							onRequiredChange={handleRequiredChange}
							onOptionEnabledChange={handleOptionEnabledChange}
						/>
					))}
					<div className="d-flex mb-4">
						<AddDomainButton onClick={() => setShowModal(true)} />
					</div>
				</>
			)}

			<Navigation
				backLabel={backLabel}
				nextLabel={nextLabel}
				saving={mutation.isLoading}
				onBack={handleBack}
				onNext={() => mutation.mutate()}
			/>

			<AddDomainModal
				show={showModal}
				availableDomains={availableDomains?.filter((d) => !domains.some((linked) => linked.domainId === d.id!))}
				isLoading={availableDomainsLoading}
				onHide={() => setShowModal(false)}
				onAdd={handleAdd}
			/>
		</>
	);
}
